import { useEffect, useMemo, useState } from 'react';
import { ApiError, errorMessage, patch } from '../../api/client';
import type { BikeModel } from '../../api/types';
import { IconPlus, IconTrash } from '../../components/icons';
import { DragHandle, SortableList } from '../../components/sortable';
import { useFeedback } from '../../components/ui';
import { SaveBar } from './SaveBar';
import { useDraft, useUnloadGuard } from '../../hooks';
import { ordinal, useT } from '../../i18n';
import { LIMITS, type TabProps } from './shared';

interface Row {
  uid: string;
  label: string;
  value: string;
}
interface Group {
  uid: string;
  title: string;
  rows: Row[];
}
interface Form {
  gears: { uid: string; value: string }[];
  groups: Group[];
}

const uid = () => crypto.randomUUID();
const fromModel = (m: BikeModel): Form => ({
  gears: m.transmission.map((value) => ({ uid: uid(), value })),
  groups: m.specs.map((g) => ({ uid: uid(), title: g.title, rows: g.rows.map((r) => ({ uid: uid(), ...r })) })),
});
/** Para comparar sin los ids internos. */
const plain = (f: Form) =>
  JSON.stringify({ g: f.gears.map((x) => x.value), s: f.groups.map((g) => [g.title, g.rows.map((r) => [r.label, r.value])]) });

export function SpecsTab({ model, onSaved, onDirty }: TabProps) {
  const { t, lang } = useT();
  const initial = useMemo(() => fromModel(model), [model]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial, (a, b) => plain(a) === plain(b));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { toast } = useFeedback();
  useUnloadGuard(dirty);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const setGroup = (id: string, fn: (g: Group) => Group) => setF((p) => ({ ...p, groups: p.groups.map((g) => (g.uid === id ? fn(g) : g)) }));

  const save = async () => {
    const submitted = f;
    setBusy(true);
    setError(null);
    try {
      const { model: saved } = await patch<{ model: BikeModel }>(`/admin/models/${model.id}`, {
        transmission: f.gears.map((g) => g.value.trim()).filter(Boolean),
        specs: f.groups
          .map((g) => ({ title: g.title.trim(), rows: g.rows.filter((r) => r.label.trim() || r.value.trim()) }))
          .filter((g) => g.title || g.rows.length)
          .map((g) => ({ title: g.title, rows: g.rows.map((r) => ({ label: r.label.trim(), value: r.value.trim() })) })),
      });
      onSaved(saved);
      markSaved(submitted);
      toast(t('Ficha técnica guardada'));
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <section className="panel panel-pad stack">
        <div>
          <h2 className="section-title">{t('Transmisión')}</h2>
          <p className="sub" style={{ margin: 0 }}>
            {t('La relación de cada marcha, en orden. Si queda vacía, la ficha no muestra la transmisión.')}
          </p>
        </div>
        <div className="row row-wrap">
          {f.gears.map((g, i) => (
            <div key={g.uid} className="input-affix" style={{ width: 150 }}>
              <span className="affix" style={{ borderLeft: '1px solid var(--line-2)', borderRight: 0, borderRadius: '8px 0 0 8px' }}>
                {ordinal(i + 1, lang)}
              </span>
              <input
                className="input"
                style={{ borderRadius: 0 }}
                value={g.value}
                inputMode="decimal"
                aria-label={t('Marcha {n}', { n: i + 1 })}
                onChange={(e) => setF((p) => ({ ...p, gears: p.gears.map((x) => (x.uid === g.uid ? { ...x, value: e.target.value } : x)) }))}
              />
              <button
                type="button"
                className="btn btn-icon"
                style={{ borderRadius: '0 8px 8px 0', height: 38 }}
                title={t('Quitar')}
                onClick={() => setF((p) => ({ ...p, gears: p.gears.filter((x) => x.uid !== g.uid) }))}
              >
                <IconTrash />
              </button>
            </div>
          ))}
          {f.gears.length < LIMITS.gears && (
            <button className="btn" onClick={() => setF((p) => ({ ...p, gears: [...p.gears, { uid: uid(), value: '' }] }))}>
              <IconPlus /> {t('Marcha')}
            </button>
          )}
        </div>
      </section>

      <section className="stack">
        <div className="row between">
          <div>
            <h2 className="section-title">{t('Grupos de la ficha')}</h2>
            <p className="sub muted" style={{ margin: 0 }}>
              {t('Los dos primeros grupos van a lo ancho y el resto, de a dos. Arrastra para cambiar el orden.')}
            </p>
          </div>
          <button className="btn" onClick={() => setF((p) => ({ ...p, groups: [...p.groups, { uid: uid(), title: '', rows: [{ uid: uid(), label: '', value: '' }] }] }))}>
            <IconPlus /> {t('Grupo')}
          </button>
        </div>
        {error?.field('specs') && <div className="notice error">{error.field('specs')}</div>}
        <SortableList items={f.groups} getId={(g) => g.uid} onReorder={(groups) => setF((p) => ({ ...p, groups }))} className="stack">
          {(g, gi, handle) => (
            <div className="panel panel-pad stack" style={{ gap: 10 }}>
              <div className="row">
                <DragHandle {...handle} />
                <input
                  className="input"
                  style={{ fontWeight: 700, maxWidth: 360 }}
                  placeholder={t('Título (p. ej. MOTOR)')}
                  value={g.title}
                  onChange={(e) => setGroup(g.uid, (x) => ({ ...x, title: e.target.value.toUpperCase() }))}
                />
                <span className="faint" style={{ fontSize: 12 }}>
                  {gi < 2 ? t('A lo ancho') : t('Media columna')}
                </span>
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setF((p) => ({ ...p, groups: p.groups.filter((x) => x.uid !== g.uid) }))}>
                  <IconTrash /> {t('Quitar grupo')}
                </button>
              </div>
              <SortableList items={g.rows} getId={(r) => r.uid} onReorder={(rows) => setGroup(g.uid, (x) => ({ ...x, rows }))} className="stack" >
                {(r, _ri, rowHandle) => (
                  <div className="row" style={{ gap: 8 }}>
                    <DragHandle {...rowHandle} />
                    <input
                      className="input"
                      placeholder={t('Dato (p. ej. Cilindrada)')}
                      value={r.label}
                      onChange={(e) => setGroup(g.uid, (x) => ({ ...x, rows: x.rows.map((y) => (y.uid === r.uid ? { ...y, label: e.target.value } : y)) }))}
                    />
                    <input
                      className="input"
                      placeholder={t('Valor (p. ej. 149 cc)')}
                      value={r.value}
                      onChange={(e) => setGroup(g.uid, (x) => ({ ...x, rows: x.rows.map((y) => (y.uid === r.uid ? { ...y, value: e.target.value } : y)) }))}
                    />
                    <button className="btn btn-ghost btn-icon" title={t('Quitar fila')} onClick={() => setGroup(g.uid, (x) => ({ ...x, rows: x.rows.filter((y) => y.uid !== r.uid) }))}>
                      <IconTrash />
                    </button>
                  </div>
                )}
              </SortableList>
              <div>
                <button className="btn btn-sm" onClick={() => setGroup(g.uid, (x) => ({ ...x, rows: [...x.rows, { uid: uid(), label: '', value: '' }] }))}>
                  <IconPlus /> {t('Fila')}
                </button>
              </div>
            </div>
          )}
        </SortableList>
      </section>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
    </div>
  );
}
